import { Position, MarkerType } from '@xyflow/react';
import { parse } from 'espree';
import axios from 'axios';
import _ from 'lodash';
import { toJS } from 'mobx';

import * as studioConstants from './constants/StudioTextConstants';
import {
  GPT_5_MODELS,
  AUTH_TYPE,
  AUTH_METHOD,
  AUTH_HEADERS,
  NODE_TYPE,
  HTTP_METHOD
} from './constants/StudioTextConstants';
import * as commonConstants from '../../common/constants';
import env from '../../common/env';
import utils from '../../common/utils';
import {
  DEFAULT_GRAPH_JS_METHOD_CODE,
  normalizeAgentMemory,
  resolveNodeDataId,
  resolveToolDataId
} from './utils/GraphEditorUtils';
import { normalizeMiddlewareForStorage } from './utils/middlewareUtils';

/**
 * Check if a model is a GPT-5 variant (case-insensitive)
 * @param {string} modelName - The model name to check
 * @returns {boolean} True if the model is a GPT-5 variant
 */
export const isGPT5Model = (modelName) => {
  if (!modelName) return false;
  const normalizedModel = modelName.toLowerCase();
  return GPT_5_MODELS.some((model) => model.toLowerCase() === normalizedModel);
};

/**
 * Converts an object to an array of { key, value } entries.
 * @param {Object} obj - Object to convert (null/undefined treated as {})
 * @returns {Array<{ key: string, value: * }>}
 */
export const convertObjectToArray = (obj) => Object.entries(obj ?? {}).map(([key, value]) => ({ key, value }));

// this helper function returns the intersection point
// of the line between the center of the intersectionNode and the target node
function getNodeIntersection(intersectionNode, targetNode) {
  // https://math.stackexchange.com/questions/1724792/an-algorithm-for-finding-the-intersection-point-between-a-center-of-vision-and-a
  const { width: intersectionNodeWidth, height: intersectionNodeHeight } = intersectionNode.measured;
  const intersectionNodePosition = intersectionNode.internals.positionAbsolute;
  const targetPosition = targetNode.internals.positionAbsolute;

  const w = intersectionNodeWidth / 2;
  const h = intersectionNodeHeight / 2;

  const x2 = intersectionNodePosition.x + w;
  const y2 = intersectionNodePosition.y + h;
  const x1 = targetPosition.x + targetNode.measured.width / 2;
  const y1 = targetPosition.y + targetNode.measured.height / 2;

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1);
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

// returns the position (top,right,bottom or right) passed node compared to the intersection point
function getEdgePosition(node, intersectionPoint) {
  const n = { ...node.internals.positionAbsolute, ...node };
  const nx = Math.round(n.x);
  const ny = Math.round(n.y);
  const px = Math.round(intersectionPoint.x);
  const py = Math.round(intersectionPoint.y);

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + n.measured.width - 1) {
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  if (py >= n.y + n.measured.height - 1) {
    return Position.Bottom;
  }

  return Position.Top;
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export function getEdgeParams(source, target) {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos
  };
}

export function createNodesAndEdges() {
  const nodes = [];
  const edges = [];
  const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

  nodes.push({ id: 'target', data: { label: 'Target' }, position: center });

  for (let i = 0; i < 8; i += 1) {
    const degrees = i * (360 / 8);
    const radians = degrees * (Math.PI / 180);
    const x = 250 * Math.cos(radians) + center.x;
    const y = 250 * Math.sin(radians) + center.y;

    nodes.push({ id: `${i}`, data: { label: 'Source' }, position: { x, y } });

    edges.push({
      id: `edge-${i}`,
      target: 'target',
      source: `${i}`,
      type: 'floating',
      markerEnd: {
        type: MarkerType.Arrow
      }
    });
  }

  return { nodes, edges };
}

// Transform a single node to backend format
export const transformNode = (node) => {
  const groupNodes = {};
  if (node?.parentId) groupNodes.parentId = node?.parentId;
  if (node?.extent) groupNodes.extent = node?.extent;
  if (node?.positionAbsolute) groupNodes.positionAbsolute = node?.positionAbsolute;
  if (node?.style) groupNodes.style = node?.style;
  if (node?.width) groupNodes.width = node?.width;
  if (node?.height) groupNodes.height = node?.height;
  // Base node structure
  const baseNode = {
    id: node.id,
    type: 'default',
    customType: node.type,
    ...(node.type === NODE_TYPE.AGENT && node.data.agentType && { agentType: node.data.agentType }),
    ...(node.type === NODE_TYPE.AGENT &&
      node.data.agentTypeConfig !== undefined && { agentTypeConfig: node.data.agentTypeConfig }),
    data: {
      label: node.data.prompt || node.data.tool_name || node.data.label || node.id,
      description: node.data.description || `Node ${node.id}`
    },
    ...groupNodes,
    position: node.position
  };

  // Add specific configurations based on node type
  if (node.type === NODE_TYPE.AGENT) {
    const modelName = node.data.config?.model?.model || 'gpt-5-mini';
    const systemPrompt = node.data.config?.systemPrompt || '';
    const userPrompt = node.data.config?.userPrompt || '';

    baseNode.data.intentType = node.data?.intentType;
    baseNode.data.agentConfig = {
      model: modelName,
      provider: node.data.config?.model?.provider || 'open-ai',
      prompt: systemPrompt ? utils.encodedData(systemPrompt) : systemPrompt,
      userPrompt: userPrompt ? utils.encodedData(userPrompt) : userPrompt,
      maxTokens: node.data.config?.model?.maxTokens || 200,
      isInternal: node.data.config?.isInternal ?? false,
      additionalMetadata: node.data.config?.additionalMetadata || {},
      otherParameters: node.data.config?.otherParameters || {},
      providerParameters: node.data.config?.providerParameters || {},
      memory: normalizeAgentMemory(node.data.config?.memory)
    };

    const normalizedMiddleware = normalizeMiddlewareForStorage(node.data.config?.middleware);
    if (normalizedMiddleware) {
      baseNode.data.agentConfig.middleware = normalizedMiddleware;
    }

    baseNode.data.parsers = node.data.config?.parsers || [];
  } else if (node.type === NODE_TYPE.SUPERVISOR) {
    const modelName = node.data.config?.model?.model || 'gpt-5-mini';

    // Get the systemPrompt and userPrompt values
    const systemPrompt =
      node.data.config?.systemPrompt ||
      // eslint-disable-next-line max-len
      "You are a routing and output agent. Given the user's last message, carefully analyse and execute only those agents and give the output as is from the agents and in a clear and separated manner and do not mix the outputs from different agents. Also do not generate anything from your end STRICTLY. In the end just print the name of each agent that were used in individual brackets.";
    const userPrompt = node.data.config?.userPrompt || '';

    baseNode.data.agentConfig = {
      model: modelName,
      provider: node.data.config?.model?.provider || 'open-ai',
      prompt: systemPrompt ? utils.encodedData(systemPrompt) : systemPrompt,
      userPrompt: userPrompt ? utils.encodedData(userPrompt) : userPrompt,
      maxTokens: node.data.config?.model?.maxTokens || 200,
      isInternal: node.data.config?.isInternal ?? false,
      additionalMetadata: node.data.config?.additionalMetadata || {},
      otherParameters: node.data.config?.otherParameters || {},
      providerParameters: node.data.config?.providerParameters || {},
      memory: normalizeAgentMemory(node.data.config?.memory)
    };
    // Add agentConfigs array to store connected agent configurations
    baseNode.data.agentConfigs = node.data.agentConfigs || [];
  } else if (node.type === NODE_TYPE.TOOL) {
    const responseFormat = node.data.config?.responseFormat;
    baseNode.data.toolId = resolveToolDataId(node.data.toolId, node.data);

    // Check if this is an MCPServer tool by checking if mcpConfig exists
    const isMCPServer = !!node.data.toolConfig?.mcpConfig;

    const rawJsMethodCode =
      node.data.config?.implementation?.code || `function ${node.data.tool_name}(params) { return params; }`;
    const rawZodSchema = JSON.stringify(node.data.config?.parameters || { type: 'object', properties: {} });

    baseNode.data.toolConfig = {
      jsMethod: node.data.config?.implementation?.name || node.data.tool_name,
      jsMethodCode: rawJsMethodCode ? utils.encodedData(rawJsMethodCode) : rawJsMethodCode,
      zodSchema: rawZodSchema ? utils.encodedData(rawZodSchema) : rawZodSchema,
      ...(responseFormat && { responseFormat }),
      // Conditional configuration based on tool type
      ...(isMCPServer
        ? {
            // MCPServer configuration - preserve existing mcpConfig as JSON object
            mcpConfig:
              typeof node.data.toolConfig?.mcpConfig === 'string'
                ? JSON.parse(node.data.toolConfig?.mcpConfig || '{}')
                : node.data.toolConfig?.mcpConfig || {}
          }
        : {
            // RestAPI configuration (default) - preserve existing apiConfig
            apiConfig: node.data.toolConfig?.apiConfig || {
              endpoint: node.data.config?.implementation?.endpoint || '',
              method: node.data.config?.implementation?.method || 'POST',
              authType: node.data.config?.implementation?.authType || 'None',
              headers: {},
              payload: {}
            }
          }),
      advancedOptions: node.data.toolConfig?.advancedOptions ||
        node.data.config?.advancedOptions || {
          timeout: 3000,
          retry: 1
        }
    };
  } else if (node.type === NODE_TYPE.SKILL) {
    const responseFormat = node.data.config?.responseFormat;
    baseNode.data.toolId = resolveNodeDataId(node.id, node.data.toolId);

    const rawJsMethodCode = node.data.config?.implementation?.code || 'async () => {\n  return "Skill prompt";\n}';
    const rawZodSchema = JSON.stringify(node.data.config?.parameters || { type: 'object', properties: {} });

    baseNode.data.toolConfig = {
      jsMethod: node.data.config?.implementation?.name || node.data.tool_name,
      jsMethodCode: rawJsMethodCode ? utils.encodedData(rawJsMethodCode) : rawJsMethodCode,
      zodSchema: rawZodSchema ? utils.encodedData(rawZodSchema) : rawZodSchema,
      ...(responseFormat && { responseFormat }),
      skillConfig: node.data.toolConfig?.skillConfig || {},
      advancedOptions: node.data.toolConfig?.advancedOptions || node.data.config?.advancedOptions || {}
    };
  } else if (node.type === NODE_TYPE.GRAPH) {
    baseNode.data.toolId = resolveNodeDataId(node.id, node.data.toolId);
    const ac = node.data.toolConfig?.apiConfig || {};
    const graphId = ac.graphId || '';
    const graphVersion = ac.graphVersion ?? null;
    let endpoint = ac.endpoint || '';
    if (graphId && graphVersion != null && !endpoint) {
      endpoint = utils.runGraphEndPoint(graphId, graphVersion);
    }
    const rawJsMethodCode = node.data.config?.implementation?.code || DEFAULT_GRAPH_JS_METHOD_CODE;
    const rawZodSchema = JSON.stringify(node.data.config?.parameters || { type: 'object', properties: {} });
    baseNode.data.toolConfig = {
      jsMethod: node.data.tool_name,
      jsMethodCode: rawJsMethodCode ? utils.encodedData(rawJsMethodCode) : rawJsMethodCode,
      zodSchema: rawZodSchema ? utils.encodedData(rawZodSchema) : rawZodSchema,
      apiConfig: {
        endpoint,
        method: ac.method || node.data.config?.implementation?.method || HTTP_METHOD.POST,
        authType: ac.authType || AUTH_TYPE.NONE,
        authMethod: ac.authMethod || AUTH_METHOD.BASIC,
        headers: ac.headers && typeof ac.headers === 'object' ? ac.headers : {},
        graphId,
        graphVersion,
        useStream: ac.useStream === true
      },
      advancedOptions: node.data.toolConfig?.advancedOptions || node.data.config?.advancedOptions || {}
    };
  }

  if (node.data.thinkingMessages) {
    baseNode.data.thinkingMessages = node.data.thinkingMessages;
  }

  return baseNode;
};

// Transform a single edge to backend format
export const transformEdge = (edge) => {
  const baseEdge = {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.data?.label || 'continue',
    sourceHandle: edge.sourceHandle || '',
    targetHandle: edge.targetHandle || ''
  };
  if (edge.data?.conditional) {
    baseEdge.condition = {
      expression: edge.data?.conditionExpression || 'true',
      evaluationType: 'javascript'
    };
  }
  return baseEdge;
};

// Configuration type mappings for Testbed
export const configTypeMappings = {
  tenantId: studioConstants.TENANT_ID_LABEL,
  tenantKey: studioConstants.TENANT_KEY_LABEL,
  tenantName: studioConstants.TENANT_NAME_LABEL,
  courseId: studioConstants.COURSE_ID_LABEL,
  pageId: studioConstants.PAGE_ID_LABEL,
  userId: studioConstants.USER_ID_LABEL,
  message: studioConstants.MESSAGE_LABEL,
  tutorName: studioConstants.TUTOR_NAME_LABEL,
  botName: studioConstants.BOT_NAME_LABEL,
  berlinEnv: studioConstants.ENVIRONMENT_LABEL,
  expandMode: studioConstants.EXPAND_MODE_LABEL,
  productModel: studioConstants.PRODUCT_MODEL_LABEL,
  enableProblemSolveFeature: studioConstants.PROBLEM_SOLVE_FEATURE_LABEL,
  showRecommendedPrompts: studioConstants.RECOMMENDED_PROMPTS_LABEL,
  AIEnableHigherOrderSuggestion: studioConstants.HIGHER_ORDER_SUGGESTION_LABEL,
  AIEnableNewValidateAnswer: studioConstants.NEW_VALIDATE_ANSWER_LABEL,
  AIEnableQuizRandomization: studioConstants.QUIZ_RANDOMIZATION_LABEL,
  AIEnableAIShowChannelVideos: studioConstants.CHANNEL_VIDEOS_LABEL,
  AIEnableAgentic: studioConstants.AGENTIC_LABEL,
  enableExplainQuizOptions: studioConstants.EXPLAIN_QUIZ_OPTIONS_LABEL,
  graphName: studioConstants.GRAPH_NAME_LABEL,
  graphId: studioConstants.GRAPH_ID_LABEL,
  channelId: studioConstants.CHANNEL_ID_LABEL,
  version: studioConstants.VERSION_LABEL,
  // Show/Hide Features
  showSummary: studioConstants.SHOW_SUMMARY_LABEL,
  showPractice: studioConstants.SHOW_PRACTICE_LABEL,
  showExplain: studioConstants.SHOW_EXPLAIN_LABEL,
  // Hidden Canned Messages Configuration
  isWelcomeMessageFromTestBed: studioConstants.WELCOME_MESSAGE_FROM_TEST_BED_LABEL,
  welcome: studioConstants.WELCOME_MESSAGE_LABEL,
  welcomeBack: studioConstants.WELCOME_BACK_MESSAGE_LABEL,
  bookTitle: studioConstants.BOOK_TITLE_LABEL,
  bookTitlePreText: studioConstants.BOOK_TITLE_PRE_TEXT_LABEL,
  bookTitleSubText: studioConstants.BOOK_TITLE_SUB_TEXT_LABEL,
  recommendationsHeading: studioConstants.RECOMMENDATIONS_HEADING_LABEL,
  ctaButtonHeadingWithRecommendationPills: studioConstants.CTA_BUTTON_HEADING_WITH_PILLS_LABEL,
  ctaButtonHeadingWithOutRecommendationPills: studioConstants.CTA_BUTTON_HEADING_WITHOUT_PILLS_LABEL,
  newTopic: studioConstants.NEW_TOPIC_LABEL,
  newTopicSubMessage: studioConstants.NEW_TOPIC_SUB_MESSAGE_LABEL,
  autoOpenHeadingMessage: studioConstants.AUTO_OPEN_HEADING_MESSAGE_LABEL,
  autoOpenWelcomeMessage: studioConstants.AUTO_OPEN_WELCOME_MESSAGE_LABEL,
  ctaButtonHeadingQuizStopMode: studioConstants.CTA_BUTTON_HEADING_QUIZ_STOP_MODE_LABEL,
  customProperties: studioConstants.CUSTOM_PROPERTIES
};

export const buildRequestPartsFromPayload = (event) => {
  const raw = event.payload || {};
  let payload = raw.tenantId ? _.omit(raw, studioConstants.TENANT_ID) : raw;
  if (payload && Object.prototype.hasOwnProperty.call(payload, 'template')) {
    payload = _.omit(payload, 'template');
  }
  let headers = raw.tenantId ? { [studioConstants.BERLIN_TENANT_ID_HEADER]: raw.tenantId } : {};
  headers = Object.keys(headers).length > 0 ? headers : undefined;
  return { payload, headers };
};

export const buildHeadersWithTenantId = (event) => {
  let headers = event.tenantId ? { [studioConstants.BERLIN_TENANT_ID_HEADER]: event.tenantId } : {};
  headers = Object.keys(headers).length > 0 ? headers : undefined;
  return headers;
};

/**
 * Fetches a system-to-system token directly from the AI Admin API.
 * Mirrors GetSys2sysTokenService without the Framework event bus.
 * Supports Stack B via URL params (?stackB=true&env=stg|prod).
 *
 * @param {boolean} [forProd] - When true, requests prod stack token (?prod=true).
 *   When omitted, resolves from Stack B URL: isStackB && env === 'prod'.
 * @param {string} msalToken - User MSAL access token for Authorization header
 * @returns {Promise<string|null>} Sys2sys token or null if missing from response
 */
export const fetchSysToSysToken = async (forProd, msalToken) => {
  const { isStackB, env: stackBEnv } = utils.getStackBParams();
  const baseUrl = isStackB ? env.AI_ADMIN_API_BASE_URL_STACKB : env.AI_ADMIN_API_BASE_URL;

  const resolvedForProd = forProd !== undefined ? Boolean(forProd) : isStackB && stackBEnv === commonConstants.ENV_PROD;

  const queryParam = resolvedForProd ? '?prod=true' : '';

  const response = await axios.get(`${baseUrl}/${commonConstants.GET_SYSTEM_TOKEN_URL}${queryParam}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${msalToken}`
    }
  });
  return response?.data?.data?.token ?? null;
};

/**
 * Get topic count configuration based on level
 * @param {string} level - The level type (e.g., 'section', 'sub-section')
 * @returns {Object} Object with minTopicCount and maxTopicCount (defaults to 2 and 6 if level not found)
 */
export const getTopicCountByLevel = (level) => {
  if (!level) {
    return {
      minTopicCount: 2,
      maxTopicCount: 6
    };
  }

  const config = studioConstants.TOPIC_COUNT_CONFIG[level];

  if (!config) {
    return {
      minTopicCount: 2,
      maxTopicCount: 6
    };
  }

  return {
    minTopicCount: config.minTopicCount,
    maxTopicCount: config.maxTopicCount
  };
};

export const validateJavaScript = (code) => {
  const result = {
    isValid: false,
    errors: [],
    warnings: []
  };

  // Step 1: Empty check
  if (!code || !code.trim()) {
    result.errors.push('JavaScript code cannot be empty');
    return result;
  }

  const trimmedCode = code.trim();

  // Step 2: Syntax check using Espree
  try {
    // Wrap code in parentheses if it’s an arrow function to make it parsable
    const isArrowFunction = /^\s*(async\s*)?\([^)]*\)\s*=>/.test(trimmedCode);
    const parseCode = isArrowFunction ? `(${trimmedCode})` : trimmedCode;

    parse(parseCode, {
      ecmaVersion: 'latest',
      sourceType: 'script',
      ecmaFeatures: {
        globalReturn: true
      }
    });
  } catch (err) {
    result.errors.push(`Syntax error: ${err.message}`);
    return result;
  }

  // Step 3: Function structure validation
  const functionPatterns = [
    /^\s*(async\s+)?function\s+\w+\s*\([^)]*\)\s*\{[\s\S]*\}\s*$/, // function declaration
    /^\s*(?:const|let|var)\s+\w+\s*=\s*(async\s+)?\([^)]*\)\s*=>\s*\{[\s\S]*\}\s*$/, // assigned arrow function
    /^\s*\([^)]*\)\s*=>\s*\{[\s\S]*\}\s*$/, // bare arrow function
    /^\s*async\s*\([^)]*\)\s*=>\s*\{[\s\S]*\}\s*$/ // async arrow function
  ];

  const isValidFunction = functionPatterns.some((pattern) => pattern.test(trimmedCode));

  if (!isValidFunction) {
    result.errors.push('Code must be a valid JavaScript function (declaration, expression, or arrow function)');
    return result;
  }

  // Step 4: Dangerous pattern detection
  const dangerousPatterns = [
    { pattern: /eval\s*\(/, message: 'eval() is not allowed for security reasons' },
    { pattern: /Function\s*\(/, message: 'Function constructor is not allowed for security reasons' },
    { pattern: /setTimeout\s*\(/, message: 'setTimeout() should be avoided in tool implementations' },
    { pattern: /setInterval\s*\(/, message: 'setInterval() should be avoided in tool implementations' },
    { pattern: /document\./, message: 'DOM manipulation is not allowed in tool implementations' },
    { pattern: /window\./, message: 'Window object access is not allowed in tool implementations' },
    { pattern: /global\./, message: 'Global object access should be avoided' },
    { pattern: /process\./, message: 'Process object access should be avoided' },
    { pattern: /require\s*\(/, message: 'require() is not allowed, use import statements instead' },
    {
      pattern: /import\s+.*\s+from\s+['"][^'"]+['"]/,
      message: 'Dynamic imports should be avoided'
    }
  ];

  // eslint-disable-next-line no-restricted-syntax
  for (const { pattern, message } of dangerousPatterns) {
    if (pattern.test(trimmedCode)) {
      result.warnings.push(message);
    }
  }

  // Step 5: Return statement check
  if (!/\breturn\b/.test(trimmedCode)) {
    result.warnings.push('Function should have a return statement');
  }

  // Step 6: Async/await mismatch
  if (/\bawait\b/.test(trimmedCode) && !/\basync\b/.test(trimmedCode)) {
    result.errors.push('Functions using await must be declared as async');
    return result;
  }

  // Step 7: Parameter structure check
  const parameterRegex = /\(\s*(\{[^}]*\}|\w+(?:\s*,\s*\w+)*)?\s*\)/;
  if (!parameterRegex.test(trimmedCode)) {
    result.warnings.push('Function should properly define parameters');
  }

  // Step 8: Unmatched braces and parentheses (fallback)
  const pairs = [
    { open: '{', close: '}', label: 'braces' },
    { open: '(', close: ')', label: 'parentheses' }
  ];
  // eslint-disable-next-line no-restricted-syntax
  for (const { open, close, label } of pairs) {
    const openCount = (trimmedCode.match(new RegExp(`\\${open}`, 'g')) || []).length;
    const closeCount = (trimmedCode.match(new RegExp(`\\${close}`, 'g')) || []).length;
    if (openCount !== closeCount) {
      result.errors.push(`Unmatched ${label} in code`);
      return result;
    }
  }

  // Step 9: Unreachable code after return
  const returnMatch = trimmedCode.match(/\breturn\b[\s\S]*?;/);
  if (returnMatch) {
    const afterReturn = trimmedCode.split(returnMatch[0])[1]?.trim();
    if (afterReturn && !afterReturn.startsWith('//') && !afterReturn.startsWith('}')) {
      result.errors.push('Code contains stray or unreachable content after return');
      return result;
    }
  }

  // If all checks passed
  result.isValid = true;
  return result;
};

// Creates a toggle function for React state setters that manage object state with boolean values.
// This is useful for toggling boolean values in an object at specific indices/keys.
export const createToggleFunction = (setState) => {
  return (index) => {
    setState((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
};

/**
 * Filters tenants based on user permissions with a specific prefix, optionally excluding specified permissions.
 * @param {Array} tenantList - List of all tenants
 * @param {Object} userPermissions - User permissions object from store
 * @param {string} permissionPrefix - Permission prefix to filter by (e.g., 'graph_')
 * @param {string|Array} [excludePermissions] - Optional permission(s) to exclude (e.g., 'graph_can_view' or ['graph_can_view', 'graph_can_edit'])
 * @returns {Array} Filtered list of tenants where user has permissions matching the prefix (excluding specified permissions if provided). Super admins get all tenants.
 */
export const getTenantsWithPermissionPrefix = (
  tenantList,
  userPermissions,
  permissionPrefix,
  excludePermissions = []
) => {
  const userPermissionDetail = toJS(userPermissions?.userPermissionDetail);

  if (!tenantList?.length || !userPermissionDetail) {
    return [];
  }

  if (userPermissionDetail.isSuperAdmin) {
    return tenantList;
  }

  const { tenantPermissions = [] } = userPermissionDetail;
  const excludeList = Array.isArray(excludePermissions) ? excludePermissions : [excludePermissions].filter(Boolean);

  return tenantList.filter((tenant) => {
    const permissions = tenantPermissions.find((t) => t.tenantId === tenant.tenantId)?.permissions || [];
    return permissions.some((perm) => perm.startsWith(permissionPrefix) && !excludeList.includes(perm));
  });
};

// import curl

// -------------- Tokenizer  --------------
export function tokenizeCurl(curlText) {
  if (!curlText || typeof curlText !== 'string') return [];

  const flattened = curlText.replace(/\\\r?\n/g, ' ').trim();

  const args = [];
  let current = '';
  let inSingle = false;
  let inDouble = false;
  let escapeNext = false;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < flattened.length; i += 1) {
    const ch = flattened[i];

    if (escapeNext) {
      current += ch;
      escapeNext = false;
    } else if (ch === '\\') {
      if (inSingle) {
        current += '\\';
      } else {
        escapeNext = true;
      }
    } else if (ch === "'" && !inDouble) {
      inSingle = !inSingle;
    } else if (ch === '"' && !inSingle) {
      inDouble = !inDouble;
    } else if (!inSingle && !inDouble && /\s/.test(ch)) {
      if (current) {
        args.push(current);
        current = '';
      }
    } else {
      current += ch;
    }
  }
  if (current) args.push(current);
  return args;
}

function parseHeaderLine(line) {
  const idx = line.indexOf(':');
  if (idx === -1) return null;
  const key = line.slice(0, idx).trim();
  const value = line.slice(idx + 1).trim();
  if (!key) return null;
  return { key, value };
}

function looksLikeUrl(token) {
  return /^(https?:)?\/\//i.test(token);
}

function tryParseJsonMaybe(text) {
  if (typeof text !== 'string') return text;
  const trimmed = text.trim();
  if (!trimmed) return '';
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      return JSON.parse(trimmed);
      // eslint-disable-next-line no-empty, no-shadow
    } catch (_) {}
  }
  return text;
}

function parseFormUrlEncoded(text) {
  try {
    const out = {};
    const usp = new URLSearchParams(text);
    // eslint-disable-next-line no-restricted-syntax
    for (const [k, v] of usp.entries()) {
      if (out[k] === undefined) out[k] = v;
      else if (Array.isArray(out[k])) out[k].push(v);
      else out[k] = [out[k], v];
    }
    return out;
  } catch {
    return null;
  }
}

function inferArrayType(arr) {
  const vals = Array.isArray(arr) ? arr : [];
  const nonNull = vals.filter((v) => v !== null && v !== undefined);
  if (nonNull.length === 0) return 'stringArray';

  const allStr = nonNull.every((v) => typeof v === 'string');
  const allNum = nonNull.every((v) => typeof v === 'number' && Number.isFinite(v));
  const allBool = nonNull.every((v) => typeof v === 'boolean');

  if (allStr) return 'stringArray';
  if (allNum) return 'numberArray';
  if (allBool) return 'booleanArray';
  return 'stringArray';
}

function coerceScalarType(v) {
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'number' && Number.isFinite(v)) return 'number';
  if (typeof v === 'string') {
    if (/^-?\d+(\.\d+)?$/.test(v)) return 'number';
    if (/^(true|false)$/i.test(v)) return 'boolean';
    return 'string';
  }
  return 'string';
}

function inferParamsFromJson(jsonVal, prefix = '') {
  const params = [];

  const pushParam = (name, type) => {
    params.push({
      name,
      type,
      description: '',
      required: true
    });
  };

  const recurse = (val, keyPath) => {
    if (val === null || val === undefined) {
      pushParam(keyPath, 'string');
      return;
    }

    if (Array.isArray(val)) {
      const t = inferArrayType(val);
      pushParam(keyPath, t);
      return;
    }

    if (typeof val === 'object') {
      let hadChild = false;
      // eslint-disable-next-line no-restricted-syntax
      for (const k of Object.keys(val)) {
        hadChild = true;
        const childKey = keyPath ? `${keyPath}.${k}` : k;
        recurse(val[k], childKey);
      }
      if (!hadChild) {
        pushParam(keyPath, 'string');
      }
      return;
    }

    pushParam(keyPath, coerceScalarType(val));
  };

  if (typeof jsonVal === 'object' && jsonVal !== null) {
    recurse(jsonVal, prefix);
  } else {
    pushParam(prefix || 'body', coerceScalarType(jsonVal));
  }

  return params;
}

export function parseCurlToApiConfig(curlText) {
  const args = tokenizeCurl(curlText);
  if (args.length === 0) {
    throw new Error('Empty cURL input');
  }

  let i = 0;
  if (args[0].toLowerCase() === 'curl') i = 1;

  let method = 'GET';
  let url = '';
  let payload = null;
  const headers = {};

  let hasData = false;
  let contentTypeFromHeader = '';

  while (i < args.length) {
    const a = args[i];

    if (a === '-X' || a === '--request') {
      method = (args[i + 1] || 'GET').toUpperCase();
      i += 2;
    } else if (a === '-H' || a === '--header') {
      const headerLine = args[i + 1] || '';
      const parsed = parseHeaderLine(headerLine);
      if (parsed) {
        headers[parsed.key] = parsed.value;
        if (parsed.key.toLowerCase() === 'content-type') {
          contentTypeFromHeader = parsed.value.toLowerCase();
        }
      }
      i += 2;
    } else if (a === '--data' || a === '--data-raw' || a === '--data-binary' || a === '-d') {
      const dataToken = args[i + 1] ?? '';
      hasData = true;
      if (dataToken.startsWith('@')) {
        payload = `__FILE_REF__:${dataToken.slice(1)}`;
      } else {
        payload = dataToken;
      }
      i += 2;
    } else if (a === '--url') {
      url = args[i + 1] || url;
      i += 2;
    } else if (!url && looksLikeUrl(a)) {
      url = a;
      i += 1;
    } else if (['--compressed', '-s', '--silent', '-L', '--location'].includes(a)) {
      i += 1;
    } else {
      i += 1;
    }
  }

  // If data was provided but no explicit method, curl implicitly does POST
  if (hasData && method === 'GET') method = 'POST';

  // -------- Normalize payload value (kept as-is) --------
  let normalizedPayload = null;
  if (payload !== null && payload !== undefined) {
    if (contentTypeFromHeader.includes('application/x-www-form-urlencoded')) {
      const parsedForm = parseFormUrlEncoded(String(payload));
      normalizedPayload = parsedForm ?? String(payload);
    } else {
      normalizedPayload = tryParseJsonMaybe(String(payload));
    }
  }

  const lowerKeys = Object.keys(headers).map((k) => k.toLowerCase());
  const getHeader = (name) =>
    headers[name] ?? headers[Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase())];

  let authType = AUTH_TYPE.NONE;
  let authMethod = AUTH_METHOD.BASIC;

  if (lowerKeys.includes(AUTH_HEADERS.AUTHORIZATION)) {
    const v = String(getHeader(AUTH_HEADERS.AUTHORIZATION) || '').toLowerCase();
    if (v.startsWith(AUTH_HEADERS.BEARER_PREFIX)) {
      authType = AUTH_TYPE.IES;
      authMethod = AUTH_METHOD.BEARER;
    }
  } else if (lowerKeys.includes(AUTH_HEADERS.X_AUTHORIZATION)) {
    authType = AUTH_TYPE.IES;
    authMethod = AUTH_METHOD.BASIC;
  } else if (lowerKeys.some((k) => k.includes(AUTH_HEADERS.API_KEY))) {
    authType = AUTH_TYPE.API_KEY;
    authMethod = AUTH_METHOD.BASIC;
  }

  // -------- Build parameters from QUERY ONLY (body is ignored) --------
  let paramsFromQuery = [];
  try {
    const u = new URL(url);
    if (u.searchParams && Array.from(u.searchParams.keys()).length > 0) {
      const qObj = {};
      // eslint-disable-next-line no-restricted-syntax
      for (const [k, v] of u.searchParams.entries()) {
        if (qObj[k] === undefined) qObj[k] = v;
        else if (Array.isArray(qObj[k])) qObj[k].push(v);
        else qObj[k] = [qObj[k], v];
      }
      paramsFromQuery = inferParamsFromJson(qObj);
    }
  } catch {
    throw new Error('Invalid cURL');
  }

  const parameters = paramsFromQuery;

  return {
    endpoint: url || '',
    method: method || 'GET',
    headers,
    payload: normalizedPayload,
    authType,
    authMethod,
    parameters
  };
}

/** Matches a RestAPI payload that is entirely one placeholder, e.g. `{{questionJSON}}`. Rejects commas/spaces inside the name. */
const REST_API_WHOLE_BODY_PAYLOAD_TEMPLATE_REGEX = /^\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}$/;

/** Finds `{{paramName}}` placeholders in payload text (whole-body or nested JSON). */
const REST_API_PAYLOAD_PLACEHOLDER_REGEX = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

function extractRestApiPayloadPlaceholders(input) {
  const text = typeof input === 'string' ? input.trim() : '';
  if (!text) {
    return [];
  }
  const names = [];
  REST_API_PAYLOAD_PLACEHOLDER_REGEX.lastIndex = 0;
  let match = REST_API_PAYLOAD_PLACEHOLDER_REGEX.exec(text);
  while (match) {
    names.push(match[1]);
    match = REST_API_PAYLOAD_PLACEHOLDER_REGEX.exec(text);
  }
  return [...new Set(names)];
}

export function isRestApiWholeBodyPayloadTemplate(input) {
  const trimmed = typeof input === 'string' ? input.trim() : '';
  return trimmed.length > 0 && REST_API_WHOLE_BODY_PAYLOAD_TEMPLATE_REGEX.test(trimmed);
}

/**
 * Validates RestAPI payload editor input.
 * Accepts JSON objects (existing behaviour) or a single whole-body placeholder template.
 * When `definedParamNames` is provided, every `{{name}}` in the payload must match a defined parameter.
 * @param {string} input
 * @param {Array<string>|null} [definedParamNames]
 * @returns {{ valid: boolean, parsed: Object|string|null, errorCode: string|null, errorMessage: string|null }}
 */
export function validateRestApiPayloadInput(input, definedParamNames = null) {
  const trimmed = typeof input === 'string' ? input.trim() : '';
  if (!trimmed) {
    return { valid: true, parsed: {}, errorCode: null, errorMessage: null };
  }

  if (Array.isArray(definedParamNames)) {
    const allowed = new Set(definedParamNames.filter(Boolean));
    const undefinedPlaceholders = extractRestApiPayloadPlaceholders(trimmed).filter((name) => !allowed.has(name));
    if (undefinedPlaceholders.length > 0) {
      return {
        valid: false,
        parsed: null,
        errorCode: commonConstants.JSON_VALIDATION_ERROR_CODE.PARSE_ERROR,
        errorMessage: `Unknown parameter(s) in payload: ${undefinedPlaceholders.join(', ')}`
      };
    }
  }

  if (isRestApiWholeBodyPayloadTemplate(trimmed)) {
    return { valid: true, parsed: trimmed, errorCode: null, errorMessage: null };
  }
  return utils.validateJsonObject(input);
}
